import { atrPct, computeAtr14 } from './atr.js';
import { decidePosition } from './decision.js';
import type { EntryDateSource, PositionRow, RawBar, TargetSource } from './types.js';

/** The broker facts every consumer can supply straight from Alpaca. */
export interface RawPositionInput {
    symbol: string;
    side: 'long' | 'short';
    qty: number;
    avgEntryPrice: number;
    currentPrice: number;
    marketValue: number;
    unrealizedPl: number;
    unrealizedPlPct: number;
    lastUpdated?: string | null;
}

/**
 * Everything a consumer can add on top of the broker facts. All optional: omit what your
 * data source can't provide and the matching cells render `—`.
 */
export interface EnrichmentInput {
    entryDate?: string | null;
    entryDateSource?: EntryDateSource | null;
    entryPriceOverride?: number | null; // e.g. Mongo buyingPrice
    daysHeld?: number | null;
    runId?: string | null;

    bars?: RawBar[] | null; // for ATR
    prevDayClose?: number | null;
    atr14Override?: number | null; // e.g. precomputed ATR from a server-side cache
    atrPctOverride?: number | null; // e.g. precomputed ATR% from a server-side cache

    // ── trading_server-computed; leave unset when unavailable ──────────────────
    stopLevel?: number | null;
    stopLossBreached?: boolean | null;
    target?: number | null;
    targetSource?: TargetSource | null;
    upsidePct?: number | null;
    upsideRemainingPct?: number | null;
    estimatedDays?: number | null;
    daysToTarget?: number | null;
    daysOverdue?: number | null;
    holdingTimeFlagged?: boolean;
    deadTradeFlagged?: boolean;
}

export const buildPositionRow = (pos: RawPositionInput, enrich?: EnrichmentInput): PositionRow => {
    const entryPrice = enrich?.entryPriceOverride ?? pos.avgEntryPrice;
    const bars = enrich?.bars ?? null;
    const atr14 = enrich?.atr14Override ?? (bars ? computeAtr14(bars) : null);
    const atrPercent =
        enrich?.atrPctOverride ?? (atr14 !== null ? atrPct(atr14, pos.currentPrice) : null);
    const prevDayClose =
        enrich?.prevDayClose ?? (bars && bars.length >= 2 ? bars[bars.length - 2].c : null);

    const base = {
        symbol: pos.symbol,
        side: pos.side,
        qty: pos.qty,
        entryPrice,
        currentPrice: pos.currentPrice,
        marketValue: pos.marketValue,
        unrealizedPl: pos.unrealizedPl,
        unrealizedPlPct: pos.unrealizedPlPct,
        lastUpdated: pos.lastUpdated ?? null,

        entryDate: enrich?.entryDate ?? null,
        entryDateSource: enrich?.entryDateSource ?? null,
        daysHeld: enrich?.daysHeld ?? null,
        runId: enrich?.runId ?? null,

        atr14,
        atrPct: atrPercent,
        prevDayClose,

        stopLevel: enrich?.stopLevel ?? null,
        stopLossBreached: enrich?.stopLossBreached ?? null,

        target: enrich?.target ?? null,
        targetSource: enrich?.targetSource ?? null,
        upsidePct: enrich?.upsidePct ?? null,
        upsideRemainingPct: enrich?.upsideRemainingPct ?? null,
        estimatedDays: enrich?.estimatedDays ?? null,
        daysToTarget: enrich?.daysToTarget ?? null,
        daysOverdue: enrich?.daysOverdue ?? null,

        holdingTimeFlagged: enrich?.holdingTimeFlagged ?? false,
        deadTradeFlagged: enrich?.deadTradeFlagged ?? false,
    };

    return { ...base, decision: decidePosition(base) };
};

export const buildPositionRows = (
    positions: RawPositionInput[],
    enrichBySymbol: Record<string, EnrichmentInput>,
): PositionRow[] => positions.map((p) => buildPositionRow(p, enrichBySymbol[p.symbol]));
